import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, ArrowLeft, Play, RotateCcw, Lock } from 'lucide-react'
import ChapterScreen from '../components/paths/ChapterScreen'

export default function PathDetailScreen() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentChapter, setCurrentChapter] = useState(1)

  // Fetch path details
  const { data: path, isLoading: pathLoading } = useQuery({
    queryKey: ['disaster-path', slug],
    queryFn: async () => {
      const response = await fetch(`/api/v1/paths/${slug}`)
      if (!response.ok) throw new Error('Failed to fetch path')
      return response.json()
    }
  })

  // Fetch user's progress
  const { data: userProgress } = useQuery({
    queryKey: ['user-path-progress', slug],
    queryFn: async () => {
      const response = await fetch(`/api/v1/paths/${slug}/progress`)
      if (!response.ok) {
        if (response.status === 404) return null // No progress yet
        throw new Error('Failed to fetch progress')
      }
      return response.json()
    }
  })

  // Start or resume path
  const startPathMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/v1/paths/${slug}/start`, {
        method: 'POST'
      })
      if (!response.ok) throw new Error('Failed to start path')
      return response.json()
    },
    onSuccess: (data) => {
      setIsPlaying(true)
      setCurrentChapter(data.current_chapter || 1)
      queryClient.invalidateQueries(['user-path-progress', slug])
    }
  })

  // Record decision
  const recordDecisionMutation = useMutation({
    mutationFn: async (decisionData) => {
      const response = await fetch(`/api/v1/paths/${slug}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(decisionData)
      })
      if (!response.ok) throw new Error('Failed to record decision')
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user-path-progress', slug])
    }
  })

  // Complete chapter
  const completeChapterMutation = useMutation({
    mutationFn: async (chapterId) => {
      const response = await fetch(`/api/v1/paths/${slug}/chapter/${chapterId}/complete`, {
        method: 'POST'
      })
      if (!response.ok) throw new Error('Failed to complete chapter')
      return response.json()
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['user-path-progress', slug])

      // Move to next chapter if available
      if (data.next_chapter) {
        setCurrentChapter(data.next_chapter)
      } else {
        // Path completed!
        setIsPlaying(false)
      }
    }
  })

  const handleStartPath = () => {
    startPathMutation.mutate()
  }

  const handleContinuePath = () => {
    setIsPlaying(true)
    setCurrentChapter(userProgress?.current_chapter || 1)
  }

  const handleResetPath = () => {
    if (confirm('Are you sure you want to reset your progress on this path?')) {
      // TODO: Add reset mutation
      setIsPlaying(false)
      setCurrentChapter(1)
    }
  }

  const handleDecisionComplete = (decisionData) => {
    recordDecisionMutation.mutate(decisionData)
  }

  const handleChapterComplete = (chapterId) => {
    completeChapterMutation.mutate(chapterId)
  }

  if (pathLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    )
  }

  if (!path) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Path Not Found</h2>
          <button
            onClick={() => navigate('/paths')}
            className="text-green-600 hover:text-green-700 font-semibold"
          >
            Back to Paths
          </button>
        </div>
      </div>
    )
  }

  // If playing, show the chapter screen
  if (isPlaying && path.chapters) {
    const chapter = path.chapters.find(c => c.chapter_number === currentChapter)

    if (!chapter) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Chapter Not Found</h2>
            <button
              onClick={() => setIsPlaying(false)}
              className="text-green-600 hover:text-green-700 font-semibold"
            >
              Back to Path
            </button>
          </div>
        </div>
      )
    }

    return (
      <ChapterScreen
        pathTitle={path.title}
        pathIcon={path.icon}
        chapter={chapter}
        chapterNumber={currentChapter}
        totalChapters={path.total_chapters}
        decisions={path.decisions?.filter(d => d.chapter_id === chapter.id) || []}
        userProgress={userProgress}
        onDecisionComplete={handleDecisionComplete}
        onChapterComplete={handleChapterComplete}
      />
    )
  }

  // Path overview screen
  const prerequisites = path.prerequisites ? JSON.parse(path.prerequisites) : []
  const completedChapters = userProgress?.completed_chapters ? JSON.parse(userProgress.completed_chapters) : []
  const isCompleted = completedChapters.length === path.total_chapters
  const isInProgress = completedChapters.length > 0 && !isCompleted

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <button
          onClick={() => navigate('/paths')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-semibold"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Paths
        </button>
      </div>

      {/* Path Header */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden"
        >
          {/* Cover */}
          <div className="relative h-64 bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
            {path.cover_image_url ? (
              <img src={path.cover_image_url} alt={path.title} className="w-full h-full object-cover" />
            ) : (
              <div className="text-9xl">{path.icon || '🎯'}</div>
            )}

            {/* Progress Badge */}
            {isInProgress && (
              <div className="absolute top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full font-semibold flex items-center gap-2">
                {completedChapters.length}/{path.total_chapters} Chapters
              </div>
            )}

            {isCompleted && (
              <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-full font-semibold">
                ✓ Completed
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                path.difficulty === 'beginner' ? 'bg-green-50 text-green-600' :
                path.difficulty === 'intermediate' ? 'bg-yellow-50 text-yellow-600' :
                'bg-red-50 text-red-600'
              }`}>
                {path.difficulty}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-600">
                {path.xp_reward} XP
              </span>
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4">{path.title}</h1>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">{path.description}</p>

            {/* Meta Info */}
            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-gray-600 mb-1">Duration</div>
                <div className="text-2xl font-bold text-gray-900">{path.estimated_duration} min</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-gray-600 mb-1">Chapters</div>
                <div className="text-2xl font-bold text-gray-900">{path.total_chapters}</div>
              </div>
            </div>

            {/* Prerequisites */}
            {prerequisites.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-yellow-600" />
                  <h3 className="font-semibold text-yellow-900">Prerequisites</h3>
                </div>
                <p className="text-yellow-800 text-sm">
                  Complete these lessons before starting this path for the best experience.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              {!isInProgress && !isCompleted && (
                <button
                  onClick={handleStartPath}
                  disabled={startPathMutation.isPending}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {startPathMutation.isPending ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Play className="w-6 h-6" />
                      Start Path
                    </>
                  )}
                </button>
              )}

              {isInProgress && (
                <>
                  <button
                    onClick={handleContinuePath}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition-colors"
                  >
                    <Play className="w-6 h-6" />
                    Continue Path
                  </button>
                  <button
                    onClick={handleResetPath}
                    className="flex items-center justify-center gap-2 bg-gray-200 text-gray-700 px-6 py-4 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                  >
                    <RotateCcw className="w-5 h-5" />
                    Reset
                  </button>
                </>
              )}

              {isCompleted && (
                <button
                  onClick={handleContinuePath}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:bg-green-700 transition-colors"
                >
                  <RotateCcw className="w-6 h-6" />
                  Play Again
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Chapters Preview */}
        {path.chapters && path.chapters.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Chapters</h2>
            <div className="space-y-3">
              {path.chapters.map((chapter, index) => {
                const isCompleted = completedChapters.includes(chapter.id)
                const isLocked = index > 0 && !completedChapters.includes(path.chapters[index - 1].id)

                return (
                  <motion.div
                    key={chapter.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className={`bg-white rounded-xl border-2 p-4 flex items-center gap-4 ${
                      isLocked ? 'opacity-50 border-gray-200' : 'border-gray-200'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                      isCompleted ? 'bg-green-100 text-green-700' :
                      isLocked ? 'bg-gray-100 text-gray-400' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {isCompleted ? '✓' : chapter.chapter_number}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{chapter.title}</h3>
                      <p className="text-sm text-gray-600">{chapter.description}</p>
                    </div>
                    {isLocked && <Lock className="w-5 h-5 text-gray-400" />}
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}
